import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import {
  updateMailSettingsRequestSchema,
  type MailSettingsView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';

export interface MailSettingsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

type ErrorKey = ApiRequestError['messageKey'];

/** The address invitation e-mails come from; empty stops sending them. */
export function MailSettingsPanel({
  locale,
  organizationId,
  csrfToken,
}: MailSettingsPanelProps): React.JSX.Element {
  const [settings, setSettings] = useState<MailSettingsView | null>(null);
  const [senderEmail, setSenderEmail] = useState('');
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const path = `/api/v1/organizations/${organizationId}/mail-settings`;

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<MailSettingsView>(path);
      setSettings(loaded);
      setSenderEmail(loaded.senderEmail ?? '');
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  const request = updateMailSettingsRequestSchema.safeParse({ senderEmail });

  async function save(): Promise<void> {
    if (settings === null || !request.success) return;
    setErrorKey(null);
    setSaved(false);
    try {
      const updated = await apiRequest<MailSettingsView>(path, {
        method: 'PUT',
        body: request.data,
        csrfToken,
        ifMatchVersion: settings.version,
      });
      setSettings(updated);
      setSenderEmail(updated.senderEmail ?? '');
      setSaved(true);
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  return (
    <Box component="section" aria-labelledby="mail-settings-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="mail-settings-title" gutterBottom>
        {translate(locale, 'mailSettings.title')}
      </Typography>
      <Typography color="text.secondary" gutterBottom>
        {translate(locale, 'mailSettings.note')}
      </Typography>
      {settings?.deliveryAvailable === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate(locale, 'mailSettings.unavailable')}
        </Alert>
      )}
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'mailSettings.saved')}
        </Alert>
      )}
      {settings === null ? (
        errorKey === null && <Typography>{translate(locale, 'mailSettings.loading')}</Typography>
      ) : (
        <Stack
          component="form"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: 'flex-start' }}
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <TextField
            size="small"
            type="email"
            label={translate(locale, 'mailSettings.address')}
            value={senderEmail}
            error={!request.success}
            helperText={request.success ? ' ' : translate(locale, 'mailSettings.invalid')}
            onChange={(event) => {
              setSaved(false);
              setSenderEmail(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 254 } }}
            sx={{ minWidth: { sm: 320 }, width: { xs: '100%', sm: 'auto' } }}
          />
          <Button type="submit" variant="contained" disabled={!request.success}>
            {translate(locale, 'mailSettings.save')}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
